import React from "react";
import { motion } from "framer-motion";
import NotifyForm from "./NotifyForm";
import BubblesBackground from "./BubblesBackground";
import { Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";

const LaunchFooter = () => {
    return (
        <footer
            id="notify"
            className="section-crimson cv-auto relative isolate overflow-hidden"
            style={{
                background:
                    "radial-gradient(ellipse at 30% 20%, #a91414 0%, #8b0000 50%, #4a0505 100%)",
            }}
            data-testid="launch-footer"
        >
            {/* Dark bubble variant over crimson */}
            <BubblesBackground variant="dark" density={22} />

            {/* Huge ornamental V watermark */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 -top-24 z-0 select-none opacity-[0.06] md:-right-32"
            >
                <span
                    className="font-serif-display text-[60vw] italic leading-none text-ivory-100 md:text-[36vw]"
                    style={{ letterSpacing: "-0.06em" }}
                >
                    V
                </span>
            </div>

            <div className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-40">
                {/* Section label */}
                <div className="relative flex items-baseline justify-between pt-6">
                    <div className="divider-gold absolute inset-x-0 top-0" />
                    <span className="text-xs uppercase tracking-[0.4em] text-ivory-100/70">
                        <span className="text-gold">§</span> Be First At The Tap
                    </span>
                    <span className="text-xs uppercase tracking-[0.4em] text-ivory-100/50">
                        Coming Soon
                    </span>
                </div>

                {/* Massive claim */}
                <motion.h2
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="font-serif-display mt-10 text-[13vw] leading-[0.9] tracking-tightest text-ivory-100 md:text-[9vw]"
                >
                    The bottle is
                    <br />
                    <em className="italic text-ivory-100/85">almost ready.</em>
                </motion.h2>

                {/* Notify + Contact grid */}
                <div className="mt-16 grid grid-cols-12 gap-8 md:mt-24">
                    <div className="col-span-12 md:col-span-7">
                        <p className="text-xs uppercase tracking-[0.4em] text-ivory-100/60">
                            · Get on the list
                        </p>
                        <h3 className="font-serif-display mt-4 max-w-lg text-4xl leading-[1.05] text-ivory-100 md:text-5xl">
                            Get one email — the day Victory lands on shelves.
                        </h3>
                        <div className="mt-10 max-w-xl">
                            <NotifyForm />
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-4 md:col-start-9">
                        <p className="text-xs uppercase tracking-[0.4em] text-ivory-100/60">
                            · Contact
                        </p>
                        <ul className="mt-6 space-y-4 text-sm text-ivory-100/90 md:text-base">
                            <li
                                className="flex items-start gap-3"
                                data-testid="contact-address"
                            >
                                <MapPin
                                    size={16}
                                    className="mt-1 shrink-0 text-ivory-100/70"
                                />
                                <span>
                                    Victory Foods &amp; Beverages Pvt Ltd
                                    <br />
                                    India
                                </span>
                            </li>
                            <li
                                className="flex items-start gap-3"
                                data-testid="contact-email"
                            >
                                <Mail
                                    size={16}
                                    className="mt-1 shrink-0 text-ivory-100/70"
                                />
                                <a
                                    href="mailto:pothireddyramsai@victoryfoods.in"
                                    className="underline-offset-4 hover:underline"
                                >
                                    pothireddyramsai@victoryfoods.in
                                </a>
                            </li>
                            <li
                                className="flex items-start gap-3"
                                data-testid="contact-phone"
                            >
                                <Phone
                                    size={16}
                                    className="mt-1 shrink-0 text-ivory-100/70"
                                />
                                <a
                                    href="tel:+918099023456"
                                    className="underline-offset-4 hover:underline"
                                >
                                    +91 80990 23456
                                </a>
                            </li>
                        </ul>

                        <div className="mt-10 flex items-center gap-3">
                            <SocialButton
                                href="#"
                                label="Instagram"
                                testid="social-instagram"
                            >
                                <Instagram size={16} />
                            </SocialButton>
                            <SocialButton
                                href="#"
                                label="LinkedIn"
                                testid="social-linkedin"
                            >
                                <Linkedin size={16} />
                            </SocialButton>
                        </div>
                    </div>
                </div>

                {/* Meta strip */}
                <div className="relative mt-24 flex flex-col items-start justify-between gap-4 pt-8 text-[10px] uppercase tracking-[0.35em] text-ivory-100/60 md:flex-row md:items-center">
                    <div className="divider-gold absolute inset-x-0 top-0" />
                    <span>© {new Date().getFullYear()} Victory Foods &amp; Beverages Pvt Ltd</span>
                    <span>Bottled with purpose · Est. India</span>
                    <span>All rights reserved</span>
                </div>
            </div>
        </footer>
    );
};

const SocialButton = ({ children, href, label, testid }) => (
    <motion.a
        href={href}
        aria-label={label}
        whileHover={{ y: -3 }}
        whileTap={{ scale: 0.94 }}
        data-testid={testid}
        className="grid h-11 w-11 place-items-center rounded-full border border-ivory-100/50 text-ivory-100 transition-colors hover:bg-ivory-100 hover:text-crimson-800"
    >
        {children}
    </motion.a>
);

export default LaunchFooter;
